import axioslib from 'axios';
import * as Sentry from '@sentry/vue';
import router from '../routes';
import { byteSize, MAX_REQUEST_BODY_BYTES } from './requestSizing';

const axios = axioslib.create({
  baseURL: process.env.VUE_APP_API_URL,
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status == 401) {
      localStorage.removeItem('token');
      // Let the login endpoint's own 401 propagate — the login form handles it
      if (error.response.config.url === 'login') {
        return Promise.reject(error);
      }
      if (error.response.config.url != 'notifications') {
        router.push('/login');
      }
      // Navigation already handled — swallow so dev overlay / callers don't see it
      return new Promise(() => {});
    }
    if (
      error.response.status &&
      error.response.status == 404 &&
      error.response.config.url != 'user'
    ) {
      router.push('/404');
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    if (token == 'undefined') {
      localStorage.removeItem('token');
    } else {
      config.headers['x-authorization'] = JSON.parse(token); // eslint-disable-line no-param-reassign
    }
  }
  // Fail fast and loud client-side rather than let the upstream WAF
  // silently block an oversized body with no application-visible error.
  // This is a safety net, not a fix - endpoints that regularly send
  // variable-size bulk arrays should batch themselves (see
  // api/shifts.js createShifts + requestSizing.js) rather than rely on
  // this ever firing.
  if (config.data && byteSize(config.data) > MAX_REQUEST_BODY_BYTES) {
    const bytes = byteSize(config.data);
    const error = new Error(
      `Request to '${config.url}' has a ${bytes} byte body, over the ${MAX_REQUEST_BODY_BYTES} byte upstream WAF limit - it would be silently blocked. Batch this request client-side instead of sending it in one call.`
    );
    error.config = config;
    // Report here rather than relying on the rejection reaching Sentry's
    // unhandled-rejection handler: reaching this branch is always a bug, and a
    // caller that catches (even just to show a message) would otherwise hide it.
    Sentry.captureException(error, {
      level: 'error',
      tags: { failure: 'request_body_too_large', endpoint: config.url },
      extra: { bytes, limit: MAX_REQUEST_BODY_BYTES, method: config.method },
    });
    return Promise.reject(error);
  }
  return config;
});

export default axios;
